import React, { useState, useReducer, useEffect } from "react";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import dayjs from "dayjs";
import { get } from "aws-amplify/api";
import DialogActions from "@mui/material/DialogActions";
import Grid from "@mui/material/Grid";
import { I18n } from "aws-amplify/utils";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import currentUserToken from "CurrentUserToken";
import Paper from '@mui/material/Paper';
import Draggable from 'react-draggable';

const reducer = (lastState, action) => {
  if (action.type === "DATA_RECEIVED") {
    const audit = action.value.map((element) => ({
      ...element,
      timestamp: typeof element.timestamp === "number"
        ? dayjs(element.timestamp).format("YYYY-MM-DD HH:mm")
        : element.timestamp,
    }));
    return { audit, fetched: true };
  }
  return lastState;
};

function PaperComponent(props) {
  const nodeRef = React.useRef(null);
  return (
    <Draggable
      nodeRef={nodeRef}
      cancel={'[class*="MuiDialogContent-root"]'}
    >
      <Paper {...props} ref={nodeRef} />
    </Draggable>
  );
}

function AddRemarkDialog({ open, onClose, onSave, alarm, language }) {
  const [state, dispatch] = useReducer(reducer, { audit: [], fetched: false });
  const [remark, setRemark] = useState("");
  const [selectedType, setSelectedType] = useState(""); // Local state for selected type

  useEffect(() => {
    const tlmGetAlarmDescription = async () => {
      try {
        const restOperation = get({
          apiName: "TelemonitoringAPI",
          path: "/pattern/type/all",
          options: {
            headers: {
              "Content-Type": "application/json",
              Authorization: `${await currentUserToken()}`,
            },
            queryParams: { type_name: "NOTE", language },
          },
        });
        const { body } = await restOperation.response;
        const response = await body.json();
        dispatch({ type: "DATA_RECEIVED", value: response });
      } catch (error) {
        console.error("API Error:", error);
      }
    };

    tlmGetAlarmDescription();
  }, [language]);

  useEffect(() => {
    if (open) {
      setRemark(""); // Clear the note field when the dialog opens
      setSelectedType(""); // Clear the select pattern field when the dialog opens
    }
  }, [open, setRemark, setSelectedType]);

  const handleSave = () => {
    onSave(remark, selectedType); // Trigger the save action in the parent component
    onClose();
  };

  // Check if at least one field is filled
  const isSaveDisabled = !(remark.trim());

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="sm" 
      fullWidth
      PaperComponent={PaperComponent}
    >
      <DialogTitle style={{ cursor: 'move' }}>{I18n.get("Add Remark")}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} style={{ marginBottom: "10px" }}>
          <Grid item xs={12}>
          <FormControl fullWidth margin="normal">
            <InputLabel>{I18n.get("Select Pattern")}</InputLabel>
            <Select
              fullWidth
              label={I18n.get("Select Pattern")}
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
            >
              {/* Add an empty MenuItem to allow clearing the selection */}
              <MenuItem value="" style={{ minHeight: "48px" }}>
                <em></em>
              </MenuItem>
              {state.audit.map((item) => (
                <MenuItem key={item.id} value={item.description}>
                  {item.description}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          </Grid>
          <Grid item xs={12}>
            <TextField
              label={I18n.get("Note")}
              fullWidth
              multiline
              rows={4}
              value={remark} // Use the passed remark prop directly
              onChange={(e) => setRemark(e.target.value)} // Update the parent state directly on change
              required // Indicate that this field is required
              error={!remark.trim()} // Show error if the note is empty
              helperText={!remark.trim() ? I18n.get("Note is required") : ""} // Provid
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions style={{ cursor: 'move' }}>
        <Button onClick={handleSave} disabled={isSaveDisabled}>{I18n.get("Save")}</Button>
        <Button onClick={onClose}>{I18n.get("Cancel")}</Button>
      </DialogActions>
    </Dialog>
  );
}

export default AddRemarkDialog;
